import type { McpServerIdentityDto } from '@terra/shared-contracts-mcp-servers';

export type AiDiscoveryIdentityBadge =
  | 'ready'
  | 'authRequired'
  | 'failed'
  | 'disabled';

export type AiDiscoveryIdentityRowModel = {
  id: string;
  primaryLabel: string;
  secondaryLabel: string;
  badge: AiDiscoveryIdentityBadge;
  kind: 'user' | 'key' | 'anonymous';
};

export function resolveAgentCredentialBadge({
  isDisabled,
}: {
  isDisabled?: boolean;
}): AiDiscoveryIdentityBadge {
  if (isDisabled) {
    return 'disabled';
  }

  return 'ready';
}

export function resolveNeedsMcpDiscoveryAuth({
  identities,
}: {
  identities: ReadonlyArray<
    Pick<McpServerIdentityDto, 'hasOauthConfig' | 'status' | 'isAnonymous'>
  >;
}): boolean {
  return identities.some(
    (identity) =>
      identity.hasOauthConfig &&
      identity.status !== 'VALID' &&
      !identity.isAnonymous,
  );
}

export function resolveMcpIdentityBadge({
  identity,
}: {
  identity: McpServerIdentityDto;
}): AiDiscoveryIdentityBadge {
  if (identity.status === 'REVOKED' || identity.status === 'EXPIRED') {
    return 'failed';
  }

  if (identity.hasOauthConfig && identity.status !== 'VALID') {
    return 'authRequired';
  }

  return 'ready';
}

export function buildAgentIdentityRows({
  credentials,
  anonymousLabel,
}: {
  credentials: Array<{
    id: string;
    username?: string | null;
    role?: string | null;
    isDisabled?: boolean;
  }>;
  anonymousLabel: string;
}): AiDiscoveryIdentityRowModel[] {
  const rows = credentials.map((credential) => ({
    id: credential.id,
    primaryLabel: credential.username?.trim() || credential.id,
    secondaryLabel: credential.role?.trim() || '',
    badge: resolveAgentCredentialBadge({
      isDisabled: credential.isDisabled,
    }),
    kind: 'user' as const,
  }));

  return [
    ...rows,
    {
      id: 'anonymous',
      primaryLabel: anonymousLabel,
      secondaryLabel: '',
      badge: 'ready',
      kind: 'anonymous',
    },
  ];
}

export function buildMcpIdentityRows({
  identities,
  defaultLabel,
  formatAuthType,
}: {
  identities: McpServerIdentityDto[];
  defaultLabel: string;
  formatAuthType?: (type: McpServerIdentityDto['type']) => string;
}): AiDiscoveryIdentityRowModel[] {
  if (identities.length === 0) {
    return [
      {
        id: 'default',
        primaryLabel: defaultLabel,
        secondaryLabel: '',
        badge: 'ready',
        kind: 'key',
      },
    ];
  }

  return identities.map((identity) => ({
    id: identity.id,
    primaryLabel: identity.label,
    secondaryLabel: formatAuthType
      ? formatAuthType(identity.type)
      : identity.type,
    badge: resolveMcpIdentityBadge({ identity }),
    kind: identity.isAnonymous ? ('anonymous' as const) : ('key' as const),
  }));
}
